import React from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Grid } from '@mui/material';

import { useGetCurrency } from 'hooks/useGetCurrency';

import { FormTableExpected } from 'components/_basic/Form/FormTableComponent/components/FormTableExpected';
import {
  UNIT_TO_VALUE_TYPE_MAP,
  UNITS,
} from 'components/_basic/Form/FormTableComponent/components/FormTableExpected/constants';
import { useHandleTotalCalculation } from 'components/BusinessToolForm/hooks/useHandleTotalCalculation';

import { formatMonetary } from 'helpers/numbers';
import { concatWithDot } from 'helpers/string';

import { ICostOfSalesTotalProps } from './types';
import { ExpensesFormTotalRow } from '../../../components';
import { useHandleTotalPercentCalculation } from '../../../hooks';
import { MONTHLY_EXPENSES_FORM_CHUNKS, MONTHLY_EXPENSES_FORM_NAMES } from '../../../MonthlyExpensesForm/utils/form';
import { costOfSalesPrefix, monthlyExpensesPrefix } from '../../../utils/form';
import { COST_OF_SALES_FORM_NAMES } from '../../utils/form';

export const CostOfSalesTotal: React.FC<ICostOfSalesTotalProps> = ({
  name,
  columns,
  firstColumnWidth,
  disabled,
  onErrorStateChange,
  scrollToElement,
}) => {
  const { t } = useTranslation();
  const currency = useGetCurrency();

  const monthlyExpenseFieldName = concatWithDot(
    monthlyExpensesPrefix,
    MONTHLY_EXPENSES_FORM_NAMES[MONTHLY_EXPENSES_FORM_CHUNKS.COST_OF_SALES_OF_PRODUCTS].EXPENSES,
  );

  const annualExpenseGrowthRateFieldName = concatWithDot(
    monthlyExpensesPrefix,
    MONTHLY_EXPENSES_FORM_NAMES[MONTHLY_EXPENSES_FORM_CHUNKS.COST_OF_SALES_OF_PRODUCTS].GROWTH_RATE,
  );

  const tableTotalName = concatWithDot(costOfSalesPrefix, COST_OF_SALES_FORM_NAMES.COST_OF_SALES_TABLE_TOTAL);

  const plannedMonthlyExpense = useWatch({ name: monthlyExpenseFieldName });
  const plannedAnnualExpenseGrowthRate = useWatch({ name: annualExpenseGrowthRateFieldName });

  const formTableExpectedColumnValue: string[] = [
    formatMonetary(plannedMonthlyExpense || 0, currency.value),
    `${plannedAnnualExpenseGrowthRate || 0}${UNIT_TO_VALUE_TYPE_MAP[UNITS.PERCENTAGE]}`,
  ];

  const handleTotalCalculate = useHandleTotalCalculation(tableTotalName);

  const saveWeightedAveragePercent = useHandleTotalPercentCalculation(annualExpenseGrowthRateFieldName);

  return (
    <Grid container gap={1.5} marginTop={1.5}>
      <ExpensesFormTotalRow
        name={name}
        columns={columns}
        expectedTotals={[plannedMonthlyExpense, plannedAnnualExpenseGrowthRate]}
        firstColumnWidth={firstColumnWidth}
        disabled={disabled}
        onErrorStateChange={onErrorStateChange}
        onTotalCalculate={handleTotalCalculate}
        onTotalPercentCalculate={saveWeightedAveragePercent}
      />
      <FormTableExpected
        rowHeader={t('financialPlan.plannedExpenses.costOfSales.expectedRowTitle')}
        onHeaderClick={scrollToElement}
        firstColumnWidth={firstColumnWidth}
        columns={formTableExpectedColumnValue}
      />
    </Grid>
  );
};
